import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { DesktopLayout } from '@/components/desktop/DesktopLayout';
import { useToast } from '@/components/common/Toast';
import { useAuth } from '@/contexts/AuthContext';
import { notificationService, NotificationWithStatus } from '@/services/notification';
import { LoginPromptModal } from '@/components/common/LoginPromptModal';
import { NotificationDetailModal } from '@/components/common/NotificationDetailModal';

export function DesktopNotificationPage() {
  const navigate = useNavigate();
  const { showToast, ToastContainer } = useToast();
  const { user } = useAuth();
  const [notifications, setNotifications] = useState<NotificationWithStatus[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [selectedNotifications, setSelectedNotifications] = useState<Set<string>>(new Set());
  const [selectedNotification, setSelectedNotification] = useState<NotificationWithStatus | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  // Load notifications
  useEffect(() => {
    const loadNotifications = async () => {
      if (!user) {
        setIsLoading(false);
        setShowLoginPrompt(true);
        return;
      }

      try {
        setIsLoading(true);
        const result = await notificationService.getNotifications(user.id);
        
        if (result.success && result.data) {
          setNotifications(result.data);
        } else {
          console.error('Failed to load notifications:', result.error);
          showToast('알림을 불러오는데 실패했습니다');
        }
      } catch (error) {
        console.error('Failed to load notifications:', error);
        showToast('알림을 불러오는데 실패했습니다');
      } finally {
        setIsLoading(false);
      }
    };

    loadNotifications();
  }, [user]);

  const handleLoginPromptClose = () => {
    setShowLoginPrompt(false);
    navigate(-1);
  };

  const handleLoginClick = () => {
    setShowLoginPrompt(false);
    navigate('/login');
  };

  const handleNotificationClick = async (notificationId: string) => {
    if (!user) {
      showToast('로그인이 필요합니다');
      return;
    }

    const target = notifications.find((n) => n.id === notificationId);
    setSelectedNotification(target || null);
    setShowDetailModal(true);

    try {
      const result = await notificationService.markAsRead(user.id, notificationId);
      
      if (result.success) {
        setNotifications(prev => 
          prev.map(notification => 
            notification.id === notificationId 
              ? { ...notification, isRead: true }
              : notification
          )
        );
      } else {
        console.error('Failed to mark as read:', result.error);
      }
    } catch (error) {
      console.error('Failed to mark as read:', error);
    }
  };

  const handleDeleteNotification = async (notificationId: string) => {
    if (!user) {
      showToast('로그인이 필요합니다');
      return;
    }

    try {
      const result = await notificationService.deleteNotification(user.id, notificationId);
      
      if (result.success) {
        setNotifications(prev => prev.filter(notification => notification.id !== notificationId));
        showToast('알림이 삭제되었습니다');
      } else {
        console.error('Failed to delete notification:', result.error);
        showToast('삭제 처리에 실패했습니다');
      }
    } catch (error) {
      console.error('Failed to delete notification:', error);
      showToast('삭제 처리에 실패했습니다');
    }
  };

  const handleDeleteAll = async () => {
    if (!user) {
      showToast('로그인이 필요합니다');
      return;
    }

    if (notifications.length === 0) {
      showToast('삭제할 알림이 없습니다');
      return;
    }

    const confirmDelete = window.confirm('모든 알림을 삭제하시겠습니까?');
    if (confirmDelete) {
      try {
        const result = await notificationService.deleteAllNotifications(user.id);
        
        if (result.success) {
          setNotifications([]);
          showToast('모든 알림이 삭제되었습니다');
        } else {
          console.error('Failed to delete all notifications:', result.error);
          showToast('전체 삭제에 실패했습니다');
        }
      } catch (error) {
        console.error('Failed to delete all notifications:', error);
        showToast('전체 삭제에 실패했습니다');
      }
    }
  };

  const handleMarkAllAsRead = async () => {
    if (!user) {
      showToast('로그인이 필요합니다');
      return;
    }

    const unreadNotifications = notifications.filter(n => !n.isRead);
    if (unreadNotifications.length === 0) {
      showToast('읽지 않은 알림이 없습니다');
      return;
    }

    try {
      // Mark all unread notifications as read
      const promises = unreadNotifications.map(n => 
        notificationService.markAsRead(user.id, n.id)
      );
      
      await Promise.all(promises);
      
      setNotifications(prev => 
        prev.map(notification => ({ ...notification, isRead: true }))
      );
      
      showToast('모든 알림을 읽음 처리했습니다');
    } catch (error) {
      console.error('Failed to mark all as read:', error);
      showToast('읽음 처리에 실패했습니다');
    }
  };

  const toggleNotificationSelection = (notificationId: string) => {
    setSelectedNotifications(prev => {
      const newSet = new Set(prev);
      if (newSet.has(notificationId)) {
        newSet.delete(notificationId);
      } else {
        newSet.add(notificationId);
      }
      return newSet;
    });
  };

  const handleDeleteSelected = async () => {
    if (!user) {
      showToast('로그인이 필요합니다');
      return;
    }

    if (selectedNotifications.size === 0) {
      showToast('선택된 알림이 없습니다');
      return;
    }

    const confirmDelete = window.confirm(`선택한 ${selectedNotifications.size}개의 알림을 삭제하시겠습니까?`);
    if (confirmDelete) {
      try {
        const promises = Array.from(selectedNotifications).map(id => 
          notificationService.deleteNotification(user.id, id)
        );
        
        await Promise.all(promises);
        
        setNotifications(prev => 
          prev.filter(notification => !selectedNotifications.has(notification.id))
        );
        setSelectedNotifications(new Set());
        showToast('선택한 알림이 삭제되었습니다');
      } catch (error) {
        console.error('Failed to delete selected notifications:', error);
        showToast('삭제 처리에 실패했습니다');
      }
    }
  };

  if (isLoading) {
    return (
      <DesktopLayout>
        <div className="flex items-center justify-center py-32">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-3 border-green-500 mx-auto mb-4"></div>
            <p className="text-xl text-gray-600">알림을 불러오는 중...</p>
          </div>
        </div>
      </DesktopLayout>
    );
  }

  const unreadCount = notifications.filter(n => !n.isRead).length;

  return (
    <DesktopLayout>
      <div className="max-w-4xl mx-auto py-8">
        <div className="bg-white rounded-xl shadow-sm">
          {/* Header */}
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-[#222222]">알림</h1>
                {unreadCount > 0 && (
                  <p className="text-sm text-gray-600 mt-1">
                    읽지 않은 알림 {unreadCount}개
                  </p>
                )}
              </div>
              
              {/* Action Buttons */}
              <div className="flex items-center gap-3">
                {selectedNotifications.size > 0 && (
                  <button
                    onClick={handleDeleteSelected}
                    className="px-4 py-2 text-sm font-medium text-white bg-red-500 rounded-lg hover:bg-red-600 transition-colors"
                  >
                    선택 삭제 ({selectedNotifications.size})
                  </button>
                )}
                <button
                  onClick={handleMarkAllAsRead}
                  className="px-4 py-2 text-sm font-medium text-[#222222] border border-[#979797] rounded-lg hover:bg-gray-50 transition-colors"
                >
                  모두 읽음
                </button>
                <button
                  onClick={handleDeleteAll}
                  className="px-4 py-2 text-sm font-medium text-[#222222] border border-[#979797] rounded-lg hover:bg-gray-50 transition-colors"
                >
                  전체 삭제
                </button>
              </div>
            </div>
          </div>

          {/* Notification List */}
          <div className="p-6">
            {notifications.length === 0 ? (
              <div className="text-center py-16">
                <div className="w-24 h-24 mx-auto mb-4 text-gray-300">
                  <svg width="96" height="96" viewBox="0 0 24 24" fill="none">
                    <path 
                      d="M15 17H20L18.595 15.595C18.4063 15.4063 18.2567 15.1822 18.1546 14.9357C18.0525 14.6891 18 14.4249 18 14.158V11C18.0002 9.75894 17.6156 8.54834 16.8992 7.53489C16.1829 6.52144 15.17 5.75496 14 5.341V5C14 4.46957 13.7893 3.96086 13.4142 3.58579C13.0391 3.21071 12.5304 3 12 3C11.4696 3 10.9609 3.21071 10.5858 3.58579C10.2107 3.96086 10 4.46957 10 5V5.341C7.67 6.165 6 8.388 6 11V14.159C6 14.697 5.786 15.214 5.405 15.595L4 17H9M15 17V18C15 18.7956 14.6839 19.5587 14.1213 20.1213C13.5587 20.6839 12.7956 21 12 21C11.2044 21 10.4413 20.6839 9.87868 20.1213C9.31607 19.5587 9 18.7956 9 18V17M15 17H9"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <p className="text-xl text-gray-600">알림이 없습니다</p>
              </div>
            ) : (
              <div className="space-y-4">
                {notifications.map((notification) => (
                  <div
                    key={notification.id}
                    className={cn(
                      "relative p-6 rounded-xl cursor-pointer transition-all hover:shadow-md",
                      notification.isRead 
                        ? "bg-gray-50 hover:bg-gray-100" 
                        : "bg-green-50 hover:bg-green-100 border border-green-200"
                    )}
                    onClick={() => handleNotificationClick(notification.id)}
                  >
                    {/* Selection Checkbox */}
                    <div className="absolute top-6 left-6">
                      <input
                        type="checkbox"
                        checked={selectedNotifications.has(notification.id)}
                        onChange={() => toggleNotificationSelection(notification.id)}
                        onClick={(e) => e.stopPropagation()}
                        className="w-5 h-5 text-green-600 rounded focus:ring-green-500"
                      />
                    </div>

                    {/* Delete Button */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteNotification(notification.id);
                      }}
                      className="absolute top-6 right-6 p-2 hover:bg-gray-200 rounded-lg transition-colors"
                    >
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 20 20"
                        fill="none"
                        className="text-gray-600"
                      >
                        <path
                          d="M15 5L5 15M5 5L15 15"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>

                    {/* Notification Content */}
                    <div className="pl-10 pr-10">
                      <div className="flex items-start justify-between mb-2">
                        <h3 className="text-lg font-semibold text-[#222222]">
                          {notification.title}
                        </h3>
                        {!notification.isRead && (
                          <span className="ml-3 px-2 py-1 bg-green-500 text-white text-xs font-medium rounded-full">
                            NEW
                          </span>
                        )}
                      </div>
                      <p className="text-gray-700 leading-relaxed">
                        {notification.message}
                      </p>
                      {notification.created_at && (
                        <p className="text-sm text-gray-500 mt-3">
                          {new Date(notification.created_at).toLocaleString('ko-KR', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      
      <ToastContainer />
      
      <LoginPromptModal
        isOpen={showLoginPrompt}
        onClose={handleLoginPromptClose}
        onLogin={handleLoginClick}
      />

      <NotificationDetailModal
        isOpen={showDetailModal}
        notification={selectedNotification}
        onClose={() => {
          setShowDetailModal(false);
          setSelectedNotification(null);
        }}
      />
    </DesktopLayout>
  );
}
